import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import testLogsApi from '../../features/test-logs/services/testLogsApi';

/**
 * Helper function to get today's date in YYYY-MM-DD format
 */
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

/**
 * Async thunk to fetch test logs
 * 
 * Fetches paginated test execution logs for a specific date with time zone handling.
 * 
 * @param {Object} params - Fetch parameters
 * @param {string} params.date - Start (or single) date in YYYY-MM-DD format
 * @param {string} params.endDate - Optional end date in YYYY-MM-DD format ('' for single-day)
 * @param {number} params.page - Page number (1-indexed at UI/Redux level)
 * @param {number} params.size - Number of items per page
 * @param {string} params.localTimeZone - Local time zone
 * @param {string} params.serverTimeZone - Server time zone
 * @param {string} params.type - Optional type filter (defaults to 'ALL')
 * @returns {Promise<Object>} Object containing logs, total_pages, and size
 */
export const fetchLogsThunk = createAsyncThunk(
  'testLogs/fetchLogs',
  async ({ date, endDate = '', page = 1, size = 20, localTimeZone, serverTimeZone, type = 'ALL' }, { rejectWithValue }) => {
    try {
      // Convert from 1-indexed (UI/Redux) to 0-indexed (API)
      // UI uses page 1, 2, 3... but API expects page 0, 1, 2...
      const apiPage = page - 1;
      const data = await testLogsApi.fetchLogs(date, endDate, apiPage, size, localTimeZone, serverTimeZone, type);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch test logs');
    }
  }
);

/**
 * Async thunk to fetch log details
 * 
 * Fetches detailed information for a specific log based on its type.
 * Routes to the appropriate API function based on log type.
 * 
 * @param {Object} params - Fetch parameters
 * @param {string} params.configId - Configuration ID of the log
 * @param {string} params.type - Log type ('SINGLE_API', 'ORCHESTRATION', or 'LOAD_TEST')
 * @returns {Promise<Object>} Log details data
 */
export const fetchLogDetailsThunk = createAsyncThunk(
  'testLogs/fetchLogDetails',
  async ({ configId, type }, { rejectWithValue }) => {
    try {
      let data;
      
      switch (type) {
        case 'SINGLE_API':
          data = await testLogsApi.fetchSingleApiDetails(configId);
          break;
        case 'ORCHESTRATION':
          data = await testLogsApi.fetchOrchestrationDetails(configId);
          break;
        case 'LOAD_TEST':
          data = await testLogsApi.fetchLoadTestDetails(configId);
          break;
        default:
          throw new Error(`Unknown log type: ${type}`);
      }
      
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch log details');
    }
  }
);

/**
 * Initial state for test logs management
 */
const initialState = {
  // Log list data
  logs: {
    data: [],
    totalPages: 1,
    pageSize: 20,
    currentPage: 1,
    loading: false,
    error: null,
    // Total count for the day
    total: 0,
    // Type-wise total counts for the entire day (not just current page)
    totalSingleApi: 0,
    totalOrchestration: 0,
    totalLoadTest: 0,
    totalAdvancedOrchestration: 0,
  },
  
  // Currently selected log
  selectedLog: null,
  
  // Detailed log information
  logDetails: {
    data: null,
    loading: false,
    error: null,
  },
  
  // Filter state
  filters: {
    startDate: getTodayDate(),
    endDate: '', // '' means single-day selection
    activeTab: 'ALL', // 'ALL' | 'SINGLE_API' | 'ORCHESTRATION' | 'LOAD_TEST'
  },
};

/**
 * Test logs slice for managing test execution history state
 */
const testLogsSlice = createSlice({
  name: 'testLogs',
  initialState,
  reducers: {
    /**
     * Set the applied date range for log filtering
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: { startDate, endDate }
     */
    setLogDateRange: (state, action) => {
      const { startDate, endDate } = action.payload || {};
      state.filters.startDate = startDate;
      // Guard for undefined endDate → default to empty string (single-day)
      state.filters.endDate = endDate === undefined ? '' : endDate;
    },

    /**
     * @deprecated Use setLogDateRange instead. Retained for backward compatibility.
     * Sets the start date and clears the end date (single-day selection).
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: date string (YYYY-MM-DD)
     */
    setLogDate: (state, action) => {
      state.filters.startDate = action.payload;
      state.filters.endDate = '';
    },

    /**
     * Set the active tab filter
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: tab ID string
     */
    setActiveTabFilter: (state, action) => {
      state.filters.activeTab = action.payload;
    },

    /**
     * Set the current page number
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: page number
     */
    setCurrentPage: (state, action) => {
      state.logs.currentPage = action.payload;
    },

    /**
     * Select a log for detailed viewing
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: log object
     */
    selectLog: (state, action) => {
      state.selectedLog = action.payload;
    },

    /**
     * Start fetching logs (set loading state)
     * @param {Object} state - Current state
     */
    fetchLogsStart: (state) => {
      state.logs.loading = true;
      state.logs.error = null;
    },

    /**
     * Successfully fetched logs
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: { logs, total_pages, size, total, total_single_api, total_orchestration, total_load_test }
     */
    fetchLogsSuccess: (state, action) => {
      state.logs.data = action.payload.logs || [];
      state.logs.totalPages = action.payload.total_pages || 1;
      state.logs.pageSize = action.payload.size || 20;
      state.logs.total = action.payload.total || 0;
      // Store type-wise total counts for the entire day
      state.logs.totalSingleApi = action.payload.total_single_api || 0;
      state.logs.totalOrchestration = action.payload.total_orchestration || 0;
      state.logs.totalLoadTest = action.payload.total_load_test || 0;
      state.logs.loading = false;
      state.logs.error = null;
    },

    /**
     * Failed to fetch logs
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: error message string
     */
    fetchLogsFailure: (state, action) => {
      state.logs.loading = false;
      state.logs.error = action.payload;
    },

    /**
     * Start fetching log details (set loading state)
     * @param {Object} state - Current state
     */
    fetchLogDetailsStart: (state) => {
      state.logDetails.loading = true;
      state.logDetails.error = null;
    },

    /**
     * Successfully fetched log details
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: log details data
     */
    fetchLogDetailsSuccess: (state, action) => {
      state.logDetails.data = action.payload;
      state.logDetails.loading = false;
      state.logDetails.error = null;
    },

    /**
     * Failed to fetch log details
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: error message string
     */
    fetchLogDetailsFailure: (state, action) => {
      state.logDetails.loading = false;
      state.logDetails.error = action.payload;
    },

    /**
     * Clear log details (when deselecting a log)
     * @param {Object} state - Current state
     */
    clearLogDetails: (state) => {
      state.logDetails.data = null;
      state.logDetails.loading = false;
      state.logDetails.error = null;
    },

    /**
     * Reset test logs state to initial values
     * @param {Object} state - Current state
     */
    resetTestLogs: (state) => {
      state.logs = initialState.logs;
      state.selectedLog = null;
      state.logDetails = initialState.logDetails;
      state.filters = {
        startDate: getTodayDate(),
        endDate: '',
        activeTab: 'ALL',
      };
    },
    setAdvancedOrchestrationCount: (state, action) => {
      state.logs.totalAdvancedOrchestration = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle fetchLogsThunk
    builder
      .addCase(fetchLogsThunk.pending, (state) => {
        state.logs.loading = true;
        state.logs.error = null;
      })
      .addCase(fetchLogsThunk.fulfilled, (state, action) => {
        state.logs.data = action.payload.logs || [];
        state.logs.totalPages = action.payload.total_pages || 1;
        state.logs.pageSize = action.payload.size || 20;
        state.logs.total = action.payload.total || 0;
        // Store type-wise total counts for the entire day
        state.logs.totalSingleApi = action.payload.total_single_api || 0;
        state.logs.totalOrchestration = action.payload.total_orchestration || 0;
        state.logs.totalLoadTest = action.payload.total_load_test || 0;
        state.logs.totalAdvancedOrchestration = action.payload.total_advanced_orchestration || 0;
        state.logs.loading = false;
        state.logs.error = null;
      })
      .addCase(fetchLogsThunk.rejected, (state, action) => {
        state.logs.loading = false;
        state.logs.error = action.payload || 'Failed to fetch logs';
      })
      // Handle fetchLogDetailsThunk
      .addCase(fetchLogDetailsThunk.pending, (state) => {
        state.logDetails.loading = true;
        state.logDetails.error = null;
      })
      .addCase(fetchLogDetailsThunk.fulfilled, (state, action) => {
        state.logDetails.data = action.payload;
        state.logDetails.loading = false;
        state.logDetails.error = null;
      })
      .addCase(fetchLogDetailsThunk.rejected, (state, action) => {
        state.logDetails.loading = false;
        state.logDetails.error = action.payload || 'Failed to fetch log details';
      });
  },
});

// Export actions
export const {
  setLogDateRange,
  setLogDate,
  setActiveTabFilter,
  setCurrentPage,
  selectLog,
  fetchLogsStart,
  fetchLogsSuccess,
  fetchLogsFailure,
  fetchLogDetailsStart,
  fetchLogDetailsSuccess,
  fetchLogDetailsFailure,
  clearLogDetails,
  resetTestLogs,
  setAdvancedOrchestrationCount,
} = testLogsSlice.actions;

// Selectors
export const selectLogs = (state) => state.testLogs.logs.data;
export const selectLogsLoading = (state) => state.testLogs.logs.loading;
export const selectLogsError = (state) => state.testLogs.logs.error;
export const selectTotalPages = (state) => state.testLogs.logs.totalPages;
export const selectCurrentPage = (state) => state.testLogs.logs.currentPage;
export const selectPageSize = (state) => state.testLogs.logs.pageSize;

export const selectSelectedLog = (state) => state.testLogs.selectedLog;

export const selectLogDetails = (state) => state.testLogs.logDetails.data;
export const selectLogDetailsLoading = (state) => state.testLogs.logDetails.loading;
export const selectLogDetailsError = (state) => state.testLogs.logDetails.error;

export const selectLogStartDate = (state) => state.testLogs.filters.startDate;
export const selectLogEndDate = (state) => state.testLogs.filters.endDate;
// @deprecated Use selectLogStartDate instead. Retained for backward compatibility.
export const selectLogDate = (state) => state.testLogs.filters.startDate;
export const selectActiveTab = (state) => state.testLogs.filters.activeTab;

/**
 * Selector to get filtered logs based on active tab
 */
export const selectFilteredLogs = (state) => {
  const logs = state.testLogs.logs.data;
  const activeTab = state.testLogs.filters.activeTab;
  
  if (activeTab === 'ALL') {
    return logs;
  }
  
  return logs.filter(log => log.type === activeTab);
};

/**
 * Selector to get tab counts - uses total counts for the entire day from backend
 * These counts represent all logs for the selected date, not just the current page
 */
export const selectTabCounts = (state) => {
  const total = state.testLogs.logs.total;
  const totalSingleApi = state.testLogs.logs.totalSingleApi;
  const totalOrchestration = state.testLogs.logs.totalOrchestration;
  const totalLoadTest = state.testLogs.logs.totalLoadTest;
  
  return {
    ALL: total,
    SINGLE_API: totalSingleApi,
    ORCHESTRATION: totalOrchestration,
    LOAD_TEST: totalLoadTest,
    ADVANCED_ORCHESTRATION: state.testLogs.logs.totalAdvancedOrchestration || 0,
  };
};

/**
 * Selector to check if pagination controls should be enabled
 */
export const selectPaginationState = (state) => {
  const currentPage = state.testLogs.logs.currentPage;
  const totalPages = state.testLogs.logs.totalPages;
  
  return {
    canGoPrevious: currentPage > 1,
    canGoNext: currentPage < totalPages,
    currentPage,
    totalPages,
  };
};

// Export reducer
export default testLogsSlice.reducer;
